import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { combineLatest, first, map, Subject, takeUntil, merge } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { CornerstoneService } from '@viewer/cornerstone.service';
import {
  CompositeKeys,
  CornerstoneTool,
  HotKey,
  HotKeySettings,
  LassoToolComponentKeyActionName,
  LassoToolComponentKeyActionNameList,
  PolylineModeType,
} from '@viewer/data';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import { IEditImageToolSettings, ToolActivationMethod } from '../data';
import { KeyInteractionService } from 'src/app/interaction';

@Component({
  selector: 'app-lasso-tool',
  templateUrl: 'lasso-tool.component.html',
  styleUrls: ['lasso-tool.component.scss'],
})
export class LassoToolComponent
  extends BaseToolComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() settings: IEditImageToolSettings = {
    actionMode: 'button',
    label: 'Lasso',
    displayIcon: true,
    disableTooltip: false,
    displayCorner: true,
    displayHotKey: true,
    hideMenuBackdrop: true,
    menuPosition: {
      x: 'before',
      y: 'above',
    },
  };
  @Input() useHotKeys = true;

  @Output() toolActivated = new EventEmitter<ToolActivationMethod>();

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  public polygonAction!: TriggerAction;

  private _unsubscribe$ = new Subject<void>();

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    private _cornerstoneService: CornerstoneService,
    private _elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService
  ) {
    super(stateManagerProjectService);
  }

  ngOnInit(): void {
    this.polygonAction = new TriggerAction(
      this.settings.displayIcon ? 'polygon-freehand' : '',
      this.settings.label,
      (_) => {
        this._onActivateToolBtnClick();
      }
    );

    this.polygonAction.isEnable = () =>
      combineLatest([
        this.isDisabled$,
        this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      ]).pipe(
        map(
          ([isDisabled, isDisabledAllToolsForChangeMask]) =>
            !isDisabled && !isDisabledAllToolsForChangeMask
        )
      );

    this.stateManagerProjectService.currentStateOfToolMenu$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentStateOfToolMenu) => {
        if (currentStateOfToolMenu !== 'LASSO' && this.menuTrigger) {
          this.menuTrigger.closeMenu();
        }
      });

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }
    
    this._cornerstoneService.closeOpenedMenu$.pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => this.menuTrigger.closeMenu());
  }

  ngAfterViewInit(): void {
    if (this.settings.actionMode === 'button') {
      super.initializeMenuBackdrop(this.menuTrigger, this._elementRef);
    }

    if (this.settings.openMenu) {
      this.menuTrigger.openMenu();
    }

    if (this.settings.closeMenuCondition$) {
      this.settings.closeMenuCondition$
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((toClose) => {
          if (toClose) {
            this.menuTrigger.closeMenu();
          }
        });
    }

    if (this.useHotKeys) {
      merge(
        this.menuTrigger.menuOpened.pipe(map(() => true)),
        this.menuTrigger.menuClosed.pipe(map(() => false))
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((isMenuOpened) => {
        this._keyInteractionService.setActivateState<LassoToolComponentKeyActionName>(
          'ExitLassoMenu',
          isMenuOpened
        );
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...LassoToolComponentKeyActionNameList,
      ]);
    }
  }

  changePolylineMode(newMode: PolylineModeType): void {
    const currentMode = this.stateManagerProjectService.currentPolylineMode;

    if (!this._checkLassoActivated() || currentMode === newMode) {
      return;
    }

    this.stateManagerProjectService.currentPolylineMode = newMode;
  }

  public onLassoActionContextMenu(event: MouseEvent): void {
    this.polygonAction
      .isEnable()
      .pipe(first())
      .subscribe((isEnable) => {
        if (!isEnable) {
          return;
        }

        event.preventDefault();
        this.menuTrigger.toggleMenu();

        if (this._checkLassoActivated()) {
          return;
        }

        this._onActivateToolBtnClick(event);
      });
  }

  private _onActivateToolBtnClick(event?: MouseEvent): void {
    if (!this.menuTrigger) {
      return;
    }

    if (!event && this.settings.actionMode === 'contextmenu') {
      this.toolActivated.emit('LMB');
    }

    if (this._checkLassoActivated()) {
      this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
      return;
    }

    this._cornerstoneService.toolActivate(
      CornerstoneTool.CustomPolylineFreehand
    );
  }

  public updateComponent(): void {
    this._cdr.markForCheck();
  }

  private _checkLassoActivated(): boolean {
    const currentTool = this.stateManagerProjectService.currentTool;
    if (!currentTool) {
      return false;
    }

    return [CornerstoneTool.CustomPolylineFreehand].includes(currentTool);
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.L,
        new HotKeySettings<LassoToolComponentKeyActionName>({
          keyActionName: 'LassoActivate',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this._stateManagerProjectService.isDisabledAllToolsForChangeMask) {
          return;
        }

        this.polygonAction.trigger();
      });

    // Subscription for "+" key
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Equal,
        new HotKeySettings<LassoToolComponentKeyActionName>({
          keyActionName: 'LassoDefaultMode',
          compositeKeys: [CompositeKeys.Shift],
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.changePolylineMode('DEFAULT');
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Minus,
        new HotKeySettings<LassoToolComponentKeyActionName>({
          keyActionName: 'LassoSubtructMode',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.changePolylineMode('SUBTRACT');
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<LassoToolComponentKeyActionName>({
          keyActionName: 'ExitLassoMenu',
          priority: 3,
          isActive: !!this.settings.openMenu
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.menuTrigger.closeMenu();
      });
  }
}
